import { API_BASE } from "@/lib/api-config";
import { authFetch } from "@/lib/authenticated-fetch";
import { getAccessToken } from "@/lib/local-auth";

export type PeriodStatus = "draft" | "open" | "locked" | "closed" | "paid";

export type PeriodRow = {
  id: string;
  start_date: string; // YYYY-MM-DD
  end_date: string; // YYYY-MM-DD
  status: PeriodStatus;
  created_at?: string;
  updated_at?: string;
};

function messageFromApiErrorBody(body: { error?: unknown }): string {
  const e = body.error;
  if (typeof e === "string") return e;
  return "Permintaan gagal";
}

function requireToken(): string {
  const token = getAccessToken();
  if (!token) throw new Error("Belum login atau token tidak tersedia.");
  return token;
}

export async function fetchPeriods(): Promise<PeriodRow[]> {
  requireToken();
  const res = await authFetch(`${API_BASE}/api/v1/periods`, {
    method: "GET",
  });
  const data = (await res.json().catch(() => ({}))) as { data?: PeriodRow[]; error?: unknown };
  if (!res.ok) throw new Error(messageFromApiErrorBody(data));
  return data.data ?? [];
}

export async function fetchPeriod(id: string): Promise<PeriodRow> {
  requireToken();
  const res = await authFetch(`${API_BASE}/api/v1/periods/${encodeURIComponent(id)}`, {
    method: "GET",
  });
  const data = (await res.json().catch(() => ({}))) as { data?: PeriodRow; error?: unknown };
  if (!res.ok) throw new Error(messageFromApiErrorBody(data));
  if (!data.data) throw new Error("Respons tidak valid");
  return data.data;
}

export async function createPeriod(body: {
  start_date: string;
  end_date: string;
  status: PeriodStatus;
}): Promise<PeriodRow> {
  requireToken();
  const res = await authFetch(`${API_BASE}/api/v1/periods`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as { data?: PeriodRow; error?: unknown };
  if (!res.ok) throw new Error(messageFromApiErrorBody(data));
  if (!data.data) throw new Error("Respons tidak valid");
  return data.data;
}

export async function updatePeriodStatus(id: string, status: PeriodStatus): Promise<PeriodRow> {
  requireToken();
  const res = await authFetch(`${API_BASE}/api/v1/periods/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ status }),
  });
  const data = (await res.json().catch(() => ({}))) as { data?: PeriodRow; error?: unknown };
  if (!res.ok) throw new Error(messageFromApiErrorBody(data));
  if (!data.data) throw new Error("Respons tidak valid");
  return data.data;
}

export async function deletePeriod(id: string): Promise<void> {
  requireToken();
  const res = await authFetch(`${API_BASE}/api/v1/periods/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
  if (res.status === 204) return;
  const data = (await res.json().catch(() => ({}))) as { error?: unknown };
  throw new Error(messageFromApiErrorBody(data));
}

